import React from 'react';
import { useForm } from 'react-hook-form';
import Button from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/forms/FormField';
import { ShowFormError } from '@/components/forms/FormError';
import { MAIL_TO } from '@/config/app.config';
import { useForgotPassword } from '@/hooks/auth/useForgotPassord';

type ForgotPasswordPayload = {
    email: string;
};

const ForgotPasswordForm: React.FC = () => {
    const { mutateAsync: forgotPassword } = useForgotPassword();
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting, isSubmitSuccessful },
    } = useForm<ForgotPasswordPayload>();

    const onSubmit = async (data: ForgotPasswordPayload) => {
        try {
            await forgotPassword({ email: data.email });
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="max-w-md mx-auto mt-10 bg-white p-6 rounded-2xl shadow-md">
            {isSubmitSuccessful ? (
                <div className="text-center text-green-600 font-medium">
                    ✅ A password reset link has been sent to your email.
                </div>
            ) : (
                <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                    <FormField label="Email" name="email" error={errors.email}>
                        <Input
                            {...register('email', {
                                required: 'Email is required',
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: 'Enter a valid email address',
                                },
                            })}
                            type="email"
                            placeholder="Enter your email"
                            autoComplete="email"
                            error={!!errors.email}
                        />
                    </FormField>

                    <ShowFormError message={errors.root?.message} />

                    <Button
                        className="w-full"
                        variant="primary"
                        size="md"
                        type="submit"
                        isLoading={isSubmitting}
                    >
                        Send Reset Link
                    </Button>

                    <div className="text-center mt-4">
                        <a
                            href="/login"
                            className="text-sm text-primary-600 hover:text-primary-500 font-medium"
                        >
                            ← Back to login
                        </a>
                    </div>
                </form>
            )}

            <div className="text-center mt-6">
                <p className="text-sm text-gray-600">
                    Need help?{' '}
                    <a href={MAIL_TO} className="font-medium text-primary-600 hover:text-primary-500">
                        Contact support
                    </a>
                </p>
            </div>
        </div>
    );
};

export default ForgotPasswordForm;
